import { useEffect, useState, useContext } from 'react'

import { Group, Tooltip, MultiSelect, Text } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { RecruitersContext } from 'src/context/RecruitersContext'
import { BULK_MEMBER_ASSIGNMENT } from 'src/graphql/applicants.graphql'
import IconUsers from 'src/icons/IconUsers'

const BulkMemberAssignmentDrawer = ({
  applicants,
  refetchQueries = [],
  onClose,
  setLoading,
  setResults,
}) => {
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([])

  const recruiters = useContext(RecruitersContext)

  // MUTATIONS:
  const [bulkMemberAssignment, { loading: bulkMemberAssignmentLoading }] =
    useMutation(BULK_MEMBER_ASSIGNMENT, {
      refetchQueries: refetchQueries,
      onCompleted: (data) => {
        const results = data?.bulkMemberAssignment?.applicants || []
        const successCount = results.filter((r) => r.success).length
        const failCount = results.filter((r) => !r.success).length

        if (setResults) {
          setResults(
            results.length
              ? results.map((result) => ({
                  applicantId: result.applicantId,
                  success: result.success,
                  errorMsg: result.errorMsg,
                }))
              : applicants.map((a) => ({
                  applicantId: a.applicantId,
                  success: true,
                }))
          )
        }

        if (failCount === 0) {
          toast('Member recruiters were reassigned successfully', 'success')
        } else if (successCount === 0) {
          toast('Failed to reassign member recruiters', 'error')
        } else {
          toast(
            `Reassigned members on ${successCount} applicants, ${failCount} failed`,
            'warning'
          )
        }
      },
      onError: () => {
        if (setResults) {
          setResults(
            applicants.map((a) => ({
              applicantId: a.applicantId,
              success: false,
              errorMsg: 'Failed to reassign member recruiters',
            }))
          )
        }
        toast(
          'Unable to reassign member recruiters, please try again.',
          'error'
        )
      },
    })

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkMemberAssignmentLoading)
    }
  }, [bulkMemberAssignmentLoading, setLoading])

  const handleSubmit = () => {
    bulkMemberAssignment({
      variables: {
        applicants: applicants.map(
          (applicant) => applicant.applicantId || applicant
        ),
        memberEmployeeIds: selectedMemberIds,
      },
    })
  }

  const isDisabled = () => {
    return applicants.length === 0 || selectedMemberIds.length === 0
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Member Recruiters</Text>
          <div className="w-[250px]">
            <MultiSelect
              value={selectedMemberIds}
              onChange={setSelectedMemberIds}
              data={recruiters}
              required
              placeholder={
                selectedMemberIds.length === 0 ? 'Select member recruiters' : ''
              }
              searchable
              size="xs"
              classNames={{
                input: 'text-xs text-right',
                dropdown: 'border-doubleNickel-gray-200',
                option:
                  'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
              }}
            />
          </div>
        </div>
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkMemberAssignmentLoading}
        />
        <Tooltip
          disabled={!isDisabled()}
          label="Please select at least one applicant and one member recruiter"
        >
          <Button
            text="Reassign"
            variant="filled"
            lefticon={<IconUsers />}
            disabled={isDisabled()}
            loading={bulkMemberAssignmentLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkMemberAssignmentDrawer
